import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { User, Save, LogOut, Globe, Mail, Download, Share, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [category, setCategory] = useState(user?.category || 'athlete');
  const [loading, setLoading] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
    // Check if iOS
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream);

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('App installed successfully!');
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateUser({ name, category });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      athlete: 'Athlete',
      golfer: 'Golfer',
      business_leader: 'Business Leader'
    };
    return labels[cat] || cat;
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-2xl" data-testid="profile-page">
        {/* Header */}
        <div>
          <h1 className="font-serif font-bold text-2xl md:text-3xl text-foreground">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </div>

        {/* Profile Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass">
            <CardHeader>
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4 p-4 bg-primary/10 rounded-xl border border-primary/20">
                <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="font-serif text-2xl font-bold text-primary">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="font-serif font-semibold text-lg text-foreground">{user?.name}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-primary/80 font-mono uppercase tracking-wider mt-1">
                    {getCategoryLabel(user?.category)}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Display Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="bg-input/50 border-transparent focus:border-primary"
                    data-testid="profile-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    value={user?.email}
                    disabled
                    className="bg-input/30 border-transparent text-muted-foreground"
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Focus Area</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-input/50 border-transparent" data-testid="profile-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="athlete">Athlete</SelectItem>
                      <SelectItem value="golfer">Golfer</SelectItem>
                      <SelectItem value="business_leader">Business Leader</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This affects your meditations, affirmations, and coach responses
                  </p>
                </div>
              </div>

              <Button
                onClick={handleSave}
                disabled={loading}
                className="w-full bg-primary text-primary-foreground rounded-full"
                data-testid="save-profile-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card/50 border-white/5">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Account Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Member Since</span>
                <span className="text-foreground">
                  {user?.created_at ? format(new Date(user.created_at), 'MMMM d, yyyy') : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-border">
                <span className="text-muted-foreground">Current Streak</span>
                <span className="text-foreground font-semibold">{user?.streak || 0} days</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-muted-foreground">Total Sessions</span>
                <span className="text-foreground font-semibold">{user?.total_sessions || 0}</span>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Logout Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card/50 border-destructive/20">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground">Sign Out</p>
                  <p className="text-sm text-muted-foreground">End your current session</p>
                </div>
                <Button
                  variant="outline"
                  onClick={logout}
                  className="border-destructive/30 text-destructive hover:bg-destructive/10 rounded-full"
                  data-testid="profile-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Download App Card */}
        {!isInstalled && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <Card className="bg-card/50 border-primary/20">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Smartphone className="h-5 w-5 text-primary" />
                  Download App
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Install MindForge on your device for quick access and offline use.
                </p>
                {isIOS ? (
                  <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-xl border border-primary/20">
                    <Share className="h-5 w-5 text-primary shrink-0" />
                    <p className="text-sm text-foreground">
                      Tap the <strong>Share</strong> button in Safari, then select <strong>"Add to Home Screen"</strong>
                    </p>
                  </div>
                ) : deferredPrompt ? (
                  <Button
                    onClick={handleInstallApp}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-full"
                    data-testid="profile-install-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Install App
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl">
                    <Download className="h-5 w-5 text-muted-foreground shrink-0" />
                    <p className="text-sm text-muted-foreground">
                      Open in Chrome or Edge browser to install the app
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Contact Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="bg-card/50 border-white/5">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <a 
                href="https://aiperformanceapps.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 py-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Globe className="h-5 w-5 text-primary" />
                <span>aiperformanceapps.com</span>
              </a>
              <a 
                href="mailto:contact@aiperformanceapps.com"
                className="flex items-center gap-3 py-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail className="h-5 w-5 text-primary" />
                <span>contact@aiperformanceapps.com</span>
              </a>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </Layout>
  );
}
